import {
  ActivePanelManager,
  InputButton,
  TextInput,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import React, { forwardRef, Ref, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DatasetGridSearchHandler } from '../DatasetGrid';

export interface SearchInputProps {
  value: string;
  width: number;
  invalid: boolean;
  currentSearchMatch: number;
  totalSearchMatches: number;
  position: { left: number };
  searchContains: boolean;
  searchCaseSensitive: boolean;
  onSearch: DatasetGridSearchHandler;
  onSearchChange: (value: string) => void;
  onCloseSearchInput: (relatedTarget?: EventTarget | null) => void;
  onToggleContains: () => void;
  onToggleCaseSensitive: () => void;
  onSearchPrev: () => void;
  onSearchNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

const messages = defineMessages({
  searchContains: {
    id: 'core.category.grid.search.contains',
    defaultMessage: 'Search within string',
  },
  searchCaseSensitive: {
    id: 'core.category.grid.search.caseSensitive',
    defaultMessage: 'Case sensitive',
  },
  searchPrev: {
    id: 'core.category.grid.search.prev',
    defaultMessage: 'Previous match (▲)',
  },
  searchNext: {
    id: 'core.category.grid.search.next',
    defaultMessage: 'Next match (▼)',
  },
});

const SearchInput = (
  {
    value,
    width,
    invalid,
    currentSearchMatch,
    totalSearchMatches,
    position,
    searchContains,
    searchCaseSensitive,
    onCloseSearchInput,
    onSearchChange,
    onToggleContains,
    onToggleCaseSensitive,
    onSearchPrev,
    onSearchNext,
    hasPrev,
    hasNext,
  }: SearchInputProps,
  ref: Ref<HTMLInputElement>
): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const wrapperRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        ActivePanelManager.helpers.ignore,
        'grid__search-input-wrapper'
      )}
      style={{ width, left: position.left }}
      onBlur={e => {
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
          onCloseSearchInput(e.relatedTarget);
        }
      }}
    >
      <TextInput
        ref={ref}
        value={value}
        invalid={invalid}
        onChange={onSearchChange}
        startSlot={
          <span
            role="presentation"
            onMouseDown={e => {
              e.preventDefault();
              (ref as React.RefObject<HTMLInputElement>).current?.focus();
            }}
          >
            <InputButton icon="search" tabIndex={-1} />
          </span>
        }
        endSlot={[
          ...(value
            ? [
                <span key="count" className="grid__search-counter">
                  {currentSearchMatch}/{totalSearchMatches}
                </span>,
              ]
            : []),
          <Tooltip
            key="case"
            label={formatMessage(messages.searchCaseSensitive)}
          >
            <button
              type="button"
              className={`grid__search-btn${searchCaseSensitive ? ' grid__search-btn--active' : ''}`}
              onClick={onToggleCaseSensitive}
            >
              Aa
            </button>
          </Tooltip>,
          <Tooltip
            key="contains"
            label={formatMessage(messages.searchContains)}
          >
            <button
              type="button"
              className={`grid__search-btn${searchContains ? ' grid__search-btn--active' : ''}`}
              onClick={onToggleContains}
            >
              *a
            </button>
          </Tooltip>,
          <Tooltip key="prev" label={formatMessage(messages.searchPrev)}>
            <button
              type="button"
              className={`grid__search-btn${!hasPrev ? ' grid__search-btn--disabled' : ''}`}
              aria-disabled={!hasPrev}
              onMouseDown={e => e.preventDefault()}
              onClick={hasPrev ? onSearchPrev : undefined}
            >
              ▲
            </button>
          </Tooltip>,
          <Tooltip key="next" label={formatMessage(messages.searchNext)}>
            <button
              type="button"
              className={`grid__search-btn${!hasNext ? ' grid__search-btn--disabled' : ''}`}
              aria-disabled={!hasNext}
              onMouseDown={e => e.preventDefault()}
              onClick={hasNext ? onSearchNext : undefined}
            >
              ▼
            </button>
          </Tooltip>,
        ]}
      />
    </div>
  );
};

export default forwardRef(SearchInput);
